import * as React from "react";
import { Navigate, Route, Routes } from "react-router-dom";
import { Summary } from "./summary";
import { Events } from "../../components/detail/events";
import { AccessReportPage } from "../../components/detail/accessReport";
import { DomainSpec } from "./spec";
import { AuditTrail } from "../../components/detail/auditTrail";
import { BasePathContext, useBasePath } from "../../reactContexts/basePathContext";
import { useEventlogs } from "../../reactHooks/useEventlog";
import { Domain } from "../../schema/types/domain/entity";
import { linksOf } from "../../services/cpln";
import { SingleNavMenuLink, toLinkButton, toLinkDivider, toLinkPage } from "../identity/useNavMenu";
import { renderEventWarningIcon } from "../identity/navMenuIcons";
import { SingleNavMenu } from "../../components/antd/layout/singleNavMenu";
import { ContentLayout } from "../../components/layout/contentLayout";
import { DomainDetailHeader } from "./header";
import { DomainConfiguration } from "./dns";
import { TagsReadonly } from "../../components/form/tagsReadonly";
import { GeoRoutePage } from "./geoRoute/GeoRoutePage";
import { useAppSelector } from "../../store/hooks";
import { selectOrg } from "../../store/contextSlice";

interface Props {
  domain: Domain;
  onEdit: any;
  refetch?: () => void;
}
export const DomainDetail: React.FC<Props> = ({ domain, onEdit, refetch }) => {
  const selfLink = linksOf(domain).self!;
  const basePath = useBasePath("/domain/:domain/*");
  const org = useAppSelector(selectOrg);
  const isNsDomain = domain.spec?.dnsMode === "ns";

  const eventlogHook = useEventlogs(selfLink, refetch);
  React.useEffect(() => {
    eventlogHook.setItemLastModified(domain.lastModified);
  }, [domain.lastModified]);

  const geoRouteEndpoints = React.useMemo(() => {
    if (!isNsDomain) return [];
    return [{ label: domain.name, endpoint: domain.name }];
  }, [isNsDomain, domain.name]);

  const links: SingleNavMenuLink[] = React.useMemo(() => {
    const items: SingleNavMenuLink[] = [
      toLinkPage("Summary", "/summary"),
      toLinkPage("Spec", "/spec"),
      toLinkPage("DNS", "/dns"),
      toLinkPage("Tags", "/tags"),
    ];
    if (isNsDomain) {
      items.push(toLinkPage("Geo Routing", "/geo-routing"));
    }
    items.push(toLinkDivider());
    items.push(
      toLinkPage("Events", "/events", { renderIcon: renderEventWarningIcon(eventlogHook.hasWarning) }),
      toLinkPage("Audit Trail", "/audit-trail"),
      toLinkPage("Access Report", "/access-report"),
      toLinkButton("Edit Domain", {
        type: "primary",
        onClick: () => {
          onEdit();
        },
      }),
    );
    return items;
  }, [eventlogHook.hasWarning, isNsDomain]);

  return (
    <BasePathContext.Provider value={basePath}>
      <DomainDetailHeader domain={domain} />
      <ContentLayout
        nav={<SingleNavMenu useDetailHeader={true} links={links} />}
        content={
          <Routes>
            <Route index element={<Navigate to={`${basePath}/summary`} replace />} />
            <Route
              path={`summary/*`}
              element={
                <div className="cpln-cards-container flex flex-col gap-4 relative">
                  <Summary domain={domain} />
                </div>
              }
            />
            <Route
              path={`spec/*`}
              element={
                <div className="cpln-cards-container flex flex-col gap-4 relative">
                  <DomainSpec domain={domain} />
                </div>
              }
            />
            <Route
              path={`dns/*`}
              element={
                <div className="cpln-cards-container flex flex-col gap-4 relative">
                  <DomainConfiguration domain={domain} />
                </div>
              }
            />
            <Route
              path={`tags/*`}
              element={
                <div className="cpln-cards-container flex flex-col gap-4 relative">
                  <TagsReadonly tags={domain.tags || {}} />
                </div>
              }
            />
            {isNsDomain ? (
              <Route
                path={`geo-routing/*`}
                element={<GeoRoutePage org={org} endpoints={geoRouteEndpoints} />}
              />
            ) : null}
            <Route
              path={`events`}
              element={
                <Events
                  kind={"domain"}
                  eventlogs={eventlogHook.eventlogs}
                  isLoading={eventlogHook.isLoading}
                  fetchEventlogs={eventlogHook.fetchEventlogs}
                  isFetching={eventlogHook.isFetching}
                  nextLink={eventlogHook.nextLink}
                />
              }
            />
            <Route
              path={`audit-trail`}
              element={
                <AuditTrail
                  kind="domain"
                  itemName={domain.name}
                  resourceId={domain.id}
                  version={domain.version}
                  selfLink={linksOf(domain).self!}
                  refetch={refetch}
                />
              }
            />
            <Route path={`access-report`} element={<AccessReportPage itemLink={linksOf(domain).self!} />} />
            <Route path={`*`} element={<Navigate to={`${basePath}/summary`} replace />} />
          </Routes>
        }
      />
    </BasePathContext.Provider>
  );
};
